import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { safeParseJson } from './utils';
import { optionalAsset } from '../frontend/services/assetResolverService';
import { BracketFormat } from '../types/eventBracketTypes';
import { TResultStandingAggregationList, TResultStandingKeystonesRankings } from '../types/eventStandingsTypes';
import {
  EventMode,
  EventType,
  ICollectionWithEventBracketSummary,
  IEventCollectionSummary,
  IEventContentData,
  IEventHubCollectionEventSummary,
  IEventHubCollectionSummary,
  IEventSummary,
  IEventWithBracketsSummary,
  LiveEventMode,
  TLatestEventData,
  TLatestEventDataSuccess
} from '../types/eventTypes';

export const getEventTypePriority = (type: EventType) => {
  switch (type) {
    case EventType.MDI:
      return 1;
    case EventType.AWC:
      return 2;
    case EventType.VARIETY_SHOW:
      return 3;
    case EventType.Legacy:
      return 4;
    default:
      return 5;
  }
};

// First come started events, then with active registration, then waiting for registration, then waiting for start, then completed, then unknown
export const getEventModePriority = (mode: EventMode) => {
  return [
    EventMode.STARTED,
    EventMode.ACTIVE,
    EventMode.WAITING_REGISTRATION,
    EventMode.WAITING_EVENT_START,
    EventMode.COMPLETED,
    EventMode.UNKNOWN
  ].indexOf(mode);
};

export function sortEvents(
  a: IEventWithBracketsSummary | IEventHubCollectionEventSummary | IEventSummary,
  b: IEventWithBracketsSummary | IEventHubCollectionEventSummary | IEventSummary
) {
  const aModePriority = getEventModePriority(a.mode);
  const bModePriority = getEventModePriority(b.mode);

  if (aModePriority !== bModePriority) {
    return aModePriority - bModePriority;
  }

  const aTypePriority = getEventTypePriority(a.type);
  const bTypePriority = getEventTypePriority(b.type);

  if (aTypePriority !== bTypePriority) {
    return aTypePriority - bTypePriority;
  }

  if (a.mode === EventMode.COMPLETED && b.mode === EventMode.COMPLETED) {
    if (a.eventEndsAt && b.eventEndsAt) {
      return moment(b.eventEndsAt).diff(moment(a.eventEndsAt));
    }

    return a.title.localeCompare(b.title);
  }

  if (a.mode === EventMode.ACTIVE && b.mode === EventMode.ACTIVE) {
    if (a.registrationEndsAt && b.registrationEndsAt) {
      const diffInDays = moment(a.registrationEndsAt).diff(moment(b.registrationEndsAt), 'days');
      if (diffInDays !== 0) {
        return diffInDays;
      }
    }

    return a.title.localeCompare(b.title);
  }

  return a.title.localeCompare(b.title);
}

function compareCollectionsForEventSort(
  a: ICollectionWithEventBracketSummary | IEventHubCollectionSummary | IEventCollectionSummary,
  b: ICollectionWithEventBracketSummary | IEventHubCollectionSummary | IEventCollectionSummary
) {
  const aEvent = a.events[0];
  const bEvent = b.events[0];

  if (!aEvent && !bEvent) {return 0;}
  if (!aEvent) {return 1;}
  if (!bEvent) {return -1;}

  const aModePriority = getEventModePriority(aEvent.mode);
  const bModePriority = getEventModePriority(bEvent.mode);

  // If both events are COMPLETED, sort by event end date (descending)
  if (aEvent.mode === EventMode.COMPLETED && bEvent.mode === EventMode.COMPLETED) {
    const aEnd = aEvent.eventEndsAt ? new Date(aEvent.eventEndsAt).getTime() : 0;
    const bEnd = bEvent.eventEndsAt ? new Date(bEvent.eventEndsAt).getTime() : 0;

    if (aEnd !== bEnd) {
      // Most recent ends first
      return bEnd - aEnd;
    }
  }

  if (aModePriority !== bModePriority) {
    return aModePriority - bModePriority;
  }

  const aTypePriority = getEventTypePriority(aEvent.type);
  const bTypePriority = getEventTypePriority(bEvent.type);

  return aTypePriority - bTypePriority;
}

export function sortCollections<
  T extends ICollectionWithEventBracketSummary | IEventHubCollectionSummary | IEventCollectionSummary
>(collections: T[]): T[] {
  return [...collections].sort(compareCollectionsForEventSort);
}

class IdGenerator {
  private static newIdPrefix = 'new_';

  constructor(private prefix: string) {
    this.prefix = prefix;
  }

  generateNewId() {
    return `${IdGenerator.newIdPrefix}_${this.prefix}_${uuidv4()}`;
  }

  isNewId(id: string | number) {
    return typeof id === 'string' && id.startsWith(IdGenerator.newIdPrefix);
  }

  getElementId(id: string | number) {
    return `${IdGenerator.newIdPrefix}_${this.prefix}-${id}`;
  }

  static isNewId(id: string | number) {
    return typeof id === 'string' && id.startsWith(IdGenerator.newIdPrefix);
  }
}

export const registrationStepIdGenerator = new IdGenerator('registrationStep');
export const eventContentIdGenerator = new IdGenerator('eventContent');

// The shape of the JSON blob stored in events_content.data. Writers (EventContentSectionEditor)
// and readers (tabHelper, EventContentSection, the unlock gate below) must share these key names.
// The persisted section-data blob viewed by the editor: the shared content-data shape
// (bracketSlug/seasonSlug/region/scope/bracketSwitcherSlugs) plus editor-managed fields.
// Composed from IEventContentData so the two can't drift apart.
export type TEventContentSectionData = IEventContentData & {
  isNew?: boolean;
  unlockAt?: string | null;
};

export function parseEventContentSectionData(data: string | null | undefined): TEventContentSectionData {
  return (data ? safeParseJson(data) : null) || {};
}

// Strict ISO only, and offset-less values are read as UTC (the editor labels the field "(UTC)").
// Returns null for anything else so callers can distinguish "no unlock time" from garbage.
export function parseEventContentUnlockAt(unlockAt: string | null | undefined): moment.Moment | null {
  if (!unlockAt) {
    return null;
  }

  const unlockMoment = moment.utc(unlockAt, moment.ISO_8601, true);
  return unlockMoment.isValid() ? unlockMoment : null;
}

// Sections may carry an `unlockAt` UTC timestamp in their JSON `data` blob; until that time
// they must be omitted from public payloads. Management endpoints stay unfiltered so event
// owners can still edit locked sections. An unparseable unlockAt fails open (visible) so a
// corrupt blob can't permanently hide content — the editor warns about invalid values instead.
export function isEventContentSectionLocked(data: string | null | undefined): boolean {
  const unlockMoment = parseEventContentUnlockAt(parseEventContentSectionData(data).unlockAt);
  return !!unlockMoment && unlockMoment.isAfter();
}

// Event pages group an ordered section list into tabs: a showTab section starts a tab and the
// non-tab sections after it render on that tab's page. A locked tab must therefore hide its
// trailing non-tab sections too — filtering it alone would re-parent them onto the previous
// visible tab and leak embargoed content.
export function omitLockedEventContentSections<T extends { data: string | null; ordinal: number }>(
  sections: T[],
  isTab: (section: T) => boolean
): T[] {
  const ordered = [...sections].sort((a, b) => a.ordinal - b.ordinal);
  const visible: T[] = [];
  let tabLocked = false;

  for (const section of ordered) {
    if (isTab(section)) {
      tabLocked = isEventContentSectionLocked(section.data);
      if (!tabLocked) {
        visible.push(section);
      }
    } else if (!tabLocked && !isEventContentSectionLocked(section.data)) {
      visible.push(section);
    }
  }

  return visible;
}

export class LiveEventParamKeyGenerator {
  private static prefix = 'hub_live_event';

  private static getParam(mode: LiveEventMode, key: string) {
    return `${this.prefix}_${mode}_${key}`;
  }

  static getRankingsLimit(mode: LiveEventMode) {
    return this.getParam(mode, 'rankings_limit');
  }

  static getCoverageInfo(mode: LiveEventMode) {
    return this.getParam(mode, 'coverage_info');
  }

  static getEventSlug(mode: LiveEventMode) {
    return this.getParam(mode, 'slug');
  }

  static getBracketSlug(mode: LiveEventMode) {
    return this.getParam(mode, 'bracket_slug');
  }

  static getVideoConfigKey(mode: LiveEventMode) {
    return this.getParam(mode, 'video_config');
  }

  static getMastheadLayoutKey(mode: LiveEventMode) {
    return this.getParam(mode, 'masthead_layout');
  }
}

export const getEventBackgroundUrl = (event?: Pick<IEventSummary, 'backgroundImage'> | null): string | null => {
  if (!event) {
    return null;
  }
  const img = event.backgroundImage;
  return img ? img.storageItem?.url || optionalAsset(img.path) : null;
};

export const isLatestEventDataSuccess = (data: TLatestEventData): data is TLatestEventDataSuccess => {
  return !('error' in data);
};

export const isEventStandingsRankings = (
  data: TResultStandingKeystonesRankings | TResultStandingAggregationList
): data is TResultStandingKeystonesRankings => {
  return 'rankings' in data;
};

export const isKeystoneRankingsBracket = (bracket: { format: BracketFormat }): boolean => {
  return [BracketFormat.THE_GREAT_PUSH, BracketFormat.TIME_TRIALS].includes(bracket.format);
};
